import React from "react";
import { Box, Typography } from "@mui/material";
import { getImageUrl, getStoryTitle } from "../lib/api";
import { androidGalleryLight, androidTabCanvas } from "../styles/androidJigsawTokens";
import AppleBottomNav from "./AppleBottomNav";

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';

/** AppShell header: brand title, uppercase, slate (light theme). */
const TITLE_SX = {
    fontFamily: FONT,
    fontWeight: 800,
    textAlign: "left",
    letterSpacing: "1.1px",
    fontSize: "0.9375rem",
    textTransform: "uppercase",
    color: "#1E293B",
    flex: 1,
    minWidth: 0,
};

/** GalleryScreen `TABS`. */
const GALLERY_TABS = [
    { value: "list", label: "List", selected: true },
    { value: "progress", label: "In Progress" },
    { value: "complete", label: "Complete" },
];

/** GalleryCategoryPills — emoji from catalog/tagIcons.js. */
const CATEGORY_PILLS = [
    { value: "all", label: "All", emoji: "🏷️", selected: true },
    { value: "country", label: "Country", emoji: "🌐" },
    { value: "fantasy", label: "Fantasy", emoji: "🧙" },
    { value: "animal", label: "Animal", emoji: "🐾" },
    { value: "education", label: "Education", emoji: "🎓" },
    { value: "city", label: "City", emoji: "🏙️" },
];

function pillSx(selected, extra = null) {
    return {
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        height: 36,
        borderRadius: "999px",
        boxSizing: "border-box",
        border: `1px solid ${selected ? androidGalleryLight.chipSelectedBorder : androidGalleryLight.chipBorder}`,
        bgcolor: selected ? androidGalleryLight.chipSelectedBg : androidGalleryLight.chipBg,
        color: selected ? "#FFFFFF" : androidGalleryLight.chipLabel,
        fontFamily: FONT,
        fontSize: "0.875rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        ...extra,
    };
}

/**
 * Light-theme Gallery tab — mirrors Android `GalleryScreen` + `PuzzleGridScreen` + `PuzzleCard`
 * (static, App Store shot).
 */
export default function AppleArchiveMenu({ items = [] }) {
    const tiles = items
        .map((item) => ({
            storyId: item?.storyId,
            title: getStoryTitle(item?.group?.story),
            imageUrl: getImageUrl(item?.group?.story),
        }))
        .filter((tile) => tile.imageUrl);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                height: "100%",
                minHeight: "100%",
                bgcolor: androidTabCanvas.library,
                color: androidGalleryLight.cardTitle,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    minHeight: 48,
                    px: 2,
                    pt: "max(env(safe-area-inset-top, 0px), 8px)",
                }}
            >
                <Typography component="h1" sx={TITLE_SX}>
                    Gallery
                </Typography>
            </Box>

            <Box sx={{ flexShrink: 0, display: "flex", gap: 1, overflow: "hidden", px: 2, pt: 1.5, pb: 0.5 }}>
                {GALLERY_TABS.map((tab) => (
                    <Box key={tab.value} sx={pillSx(Boolean(tab.selected), { px: 2, justifyContent: "center", fontWeight: 700 })}>
                        {tab.label}
                    </Box>
                ))}
            </Box>

            <Box sx={{ flexShrink: 0, display: "flex", overflow: "hidden", px: 1.5, pt: 2, pb: 1.5 }}>
                {CATEGORY_PILLS.map((opt) => (
                    <Box key={opt.value} sx={pillSx(Boolean(opt.selected), { pl: "10px", pr: "14px", mr: 1 })}>
                        <Box component="span" sx={{ width: 22, textAlign: "center", fontSize: 16, lineHeight: "20px", mr: 0.75 }} aria-hidden>
                            {opt.emoji}
                        </Box>
                        {opt.label}
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gridAutoRows: "min-content",
                    alignContent: "start",
                    gap: 2,
                    px: 2,
                    overflow: "hidden",
                }}
            >
                {tiles.map((tile, index) => (
                    <Box
                        key={tile.storyId || tile.imageUrl}
                        sx={{
                            minWidth: 0,
                            borderRadius: "16px",
                            overflow: "hidden",
                            bgcolor: androidGalleryLight.cardBg,
                            border: `1px solid ${androidGalleryLight.cardBorder}`,
                        }}
                    >
                        <Box sx={{ position: "relative", width: "100%", aspectRatio: "1 / 1", overflow: "hidden" }}>
                            <Box
                                component="img"
                                src={tile.imageUrl}
                                alt={tile.title}
                                sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    px: 1,
                                    py: "3px",
                                    borderRadius: "999px",
                                    bgcolor: "rgba(255,255,255,0.9)",
                                    color: "#1E293B",
                                    fontFamily: FONT,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: "0.4px",
                                    lineHeight: 1.3,
                                }}
                            >
                                {index % 3 === 1 ? "PREMIUM" : "FREE"}
                            </Box>
                        </Box>
                        <Typography
                            sx={{
                                px: 1.25,
                                py: 1,
                                fontFamily: FONT,
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: "17px",
                                color: androidGalleryLight.cardTitle,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {tile.title}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <AppleBottomNav activeTab="library" />
        </Box>
    );
}
